import { useBoolean, useDocumentTitle } from "usehooks-ts";
import Button from "../../../../student/components/generic/button";
import { EditVoucherLicenseModal } from "./EditVoucherLicenseModal";
import { DataTableContextProvider } from "../../DataTable/DataTableContext";
import { DataTable } from "../../DataTable/DataTable";
import { useState } from "react";
import { LearnerLicense } from "./types";
import { useQueryClient } from "@tanstack/react-query";
import { TableHeaderSelectFilter } from "../../DataTable/TableHeaderSelectFilter";

const QUERY_KEY = "listLearnerLicenses";

export const VoucherLicensesPage: React.FC = () => {
  useDocumentTitle("DMH Voucher Licenses");

  const queryClient = useQueryClient();

  const { value: showNewLicenseForm, toggle: toggleShowNewLicenseForm } =
    useBoolean(false);
  const [activeLicense, setActiveLicense] = useState<LearnerLicense | null>(
    null
  );

  const onSave = () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });

  return (
    <main className="flex flex-col gap-6 p-6">
      <div className="flex flex-col items-start gap-4">
        <header>
          <h1 className="text-2xl font-bold">Voucher Licenses</h1>
          <p>
            Create, view, and manage DeltaMath for Home licenses for parents who
            pay with vouchers, purchase orders, or other non-Stripe payment
            methods.
          </p>
        </header>
        <Button onClick={toggleShowNewLicenseForm}>
          Create a new voucher license
        </Button>
        <EditVoucherLicenseModal
          visible={showNewLicenseForm}
          onClose={toggleShowNewLicenseForm}
          onSave={() => {
            onSave();
            toggleShowNewLicenseForm();
          }}
        />
      </div>

      <DataTableContextProvider
        source="/payments/learner-licenses/list"
        queryKey={QUERY_KEY}
        sortable={["createdAt", "updatedAt"]}
        filterable={[
          "contactFirst",
          "contactLast",
          "contactEmail",
          "accessCode",
          "quoteNumber",
          "voucherNumber",
          "accepted",
        ]}
        globalFilter
        defaults={{ sortBy: [{ id: "createdAt", desc: true }] }}
        columnOverrides={{
          edit: ({ row }) => (
            <Button
              type="link"
              onClick={() =>
                setActiveLicense(row.original as unknown as LearnerLicense)
              }
            >
              <i className="far fa-edit" />
            </Button>
          ),
        }}
        filterOverrides={{
          accepted: ({ column }) => (
            <TableHeaderSelectFilter
              className="!min-w-24"
              column={column}
              choices={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
            />
          ),
        }}
        fetchOnLoad
      >
        <DataTable />

        <EditVoucherLicenseModal
          visible={!!activeLicense}
          onClose={() => setActiveLicense(null)}
          license={activeLicense ?? undefined}
          onSave={() => {
            onSave();
            setActiveLicense(null);
          }}
        />
      </DataTableContextProvider>
    </main>
  );
};
